import { useCallback, useRef } from 'react';
import openDrivePicker from '@/requests/cloud-storage/openDrivePicker';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { AiGenScreenAction, ScreenType } from '@/types/analytics';
import { InputType } from '@/types';

import type { IImageInput, IVideoInput } from '@/types';

type UpdateInput = (args: {
  id: string;
  value: Partial<IImageInput | IVideoInput>;
}) => void;

interface UseDrivePickerOptions {
  updateInput: UpdateInput;
  isAuthorized: boolean | undefined;
}

const useDrivePicker = ({
  updateInput,
  isAuthorized,
}: UseDrivePickerOptions) => {
  const pendingDriveInputId = useRef<string | null>(null);
  const pendingDriveAction = useRef<{
    id: string;
    inputType: InputType;
  } | null>(null);

  const openDrivePickerForInput = useCallback(
    async (id: string, inputType: InputType) => {
      // Guard against double-clicks or re-triggers while picker is open
      if (pendingDriveInputId.current) return;

      // Set guard immediately
      pendingDriveInputId.current = id;

      try {
        sendAiGenScreenActionAnalytics({
          action: AiGenScreenAction.UPLOAD,
          screen_type: ScreenType.GENERATOR,
        });

        const filters = inputType === InputType.VIDEO ? 'VIDEO' : 'PHOTO';
        const result = await openDrivePicker({ multiselect: false, filters });

        if (result?.files?.length) {
          const file = result.files[0];
          const sourceUrl = file.sourceUrl || file.thumbnailUrl;

          if (sourceUrl && pendingDriveInputId.current === id) {
            updateInput({
              id,
              value: { src: sourceUrl } as Partial<IImageInput | IVideoInput>,
            });
          }
        }
      } finally {
        pendingDriveInputId.current = null;
      }
    },
    [updateInput]
  );

  const handleSelectFromDrive = useCallback(
    async (id: string, inputType: InputType) => {
      if (!isAuthorized) {
        // Store pending action to open Drive after login
        pendingDriveAction.current = { id, inputType };
        openRegistrationModal();
        return;
      }

      openDrivePickerForInput(id, inputType);
    },
    [isAuthorized, openDrivePickerForInput]
  );

  return {
    handleSelectFromDrive,
    openDrivePickerForInput,
    pendingDriveAction,
  };
};

export default useDrivePicker;
